import { createPortal } from "react-dom";
import { LoaderCircle, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import type {
  BulkOption,
  BulkPatchWire,
  BulkTargetBrand,
  BulkTargetSummary
} from "@/shared/api/api-client";
import { errorMessage } from "@/shared/api/http";
import { usePolarityChoices } from "@/shared/components/polarity-choices";
import { useDismiss } from "@/shared/hooks/useDismiss";
import { APPROACH_CHOICES } from "@/features/mentions/mention-filter-criteria";
import { CatalogMultiSelect } from "@/features/mentions/MentionClassificationEditor";
import {
  KEEP,
  buildBulkPatch,
  bulkPatchSize,
  emptyBulkDialogState,
  highlightOptions,
  isBulkPatchEmpty,
  type BulkDialogState,
  type ListField
} from "@/features/mentions/bulk-patch";
import { Select } from "@/shared/components/Select";

const SOURCE_LABELS: Record<string, string> = { press: "imprensa", network: "redes" };

/**
 * O editor de classificação EM LOTE (2026-09-21, pedido da galera de
 * negócio).
 *
 * Difere do editor individual em duas coisas, e as duas vêm de o alvo
 * ser MUITAS menções: (1) cada dimensão começa em "não mexer" — o lote
 * não tem valor atual pra pré-preencher, e um select que já viesse com
 * "Positivo" aplicaria uma correção que ninguém pediu; (2) as listas
 * têm MODO (adicionar, remover, substituir), porque cada menção tem a
 * sua lista e "a lista final" não existe aqui. A tradução do modo é do
 * servidor, por menção. O patch em si é montado por `buildBulkPatch`,
 * que é puro e testado.
 *
 * **Subtemas, mensagens-chave e porta-vozes: só o que existe em TODAS
 * as marcas e fontes do alvo** (decisão da dona, 2026-11-04; substitui o
 * "só com uma marca" de 2026-10-31). Subtema e porta-voz são cadastro
 * por marca, mensagem-chave por marca E fonte. Quem diz o que fecha em
 * todas é o servidor, sobre o mesmo alvo do envio (`loadTarget`): cada
 * opção é um nome com os ids de cada marca/fonte, e marcar a opção manda
 * todos — o servidor aplica em cada menção o id dela. O que não existe
 * em alguma combinação não aparece.
 *
 * **"Subtema em destaque" só oferece subtema que TODAS as menções têm**
 * (decisão da dona, 2026-10-03), e a conta acompanha o que o próprio
 * lote faz com os subtemas (`highlightOptions`).
 */
export function BulkClassificationDialog({
  count,
  targetLabel,
  loadTarget,
  onSubmit,
  onClose
}: {
  /** `null` = "todas do filtro" com o total ainda sendo calculado. */
  count: number | null;
  targetLabel: string;
  /** As marcas e fontes do alvo, os subtemas presentes em todas as
   *  menções e as opções em comum de cada lista. */
  loadTarget: () => Promise<BulkTargetSummary>;
  onSubmit: (patch: BulkPatchWire) => Promise<void>;
  onClose: () => void;
}) {
  const [state, setState] = useState<BulkDialogState>(emptyBulkDialogState);
  const polarityChoices = usePolarityChoices();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // `null` = ainda conferindo. Uma vez, ao abrir: o alvo não muda com o
  // diálogo aberto, e a função do pai é recriada a cada render dele.
  const [target, setTarget] = useState<BulkTargetSummary | null>(null);
  const [targetError, setTargetError] = useState<string | null>(null);
  const loadTargetRef = useRef(loadTarget);

  useEffect(() => {
    let alive = true;
    loadTargetRef
      .current()
      .then(found => {
        if (alive) setTarget(found);
      })
      .catch(failure => {
        if (!alive) return;
        setTargetError(
          errorMessage(failure, "Não foi possível conferir de que marcas e fontes são as menções.")
        );
      });
    return () => {
      alive = false;
    };
  }, []);

  useDismiss(true, () => {
    if (!saving) onClose();
  });

  // As listas só abrem depois que o servidor disse o que fecha em todas.
  const listsLocked = target === null;
  const themeOptions = target?.themes ?? [];
  const keyMessageOptions = target?.key_messages ?? [];
  const spokespersonOptions = target?.spokespersons ?? [];

  const targetNote = (() => {
    if (targetError !== null) return targetError;
    if (target === null) return "Conferindo de que marcas e fontes são as menções…";
    const brands: BulkTargetBrand[] = target.brands;
    const names = brands.map(brand =>
      brand.competitor_id === null
        ? brand.brand_name
        : `${brand.brand_name} (concorrente ${brand.competitor_name ?? ""})`
    );
    const shown = names.slice(0, 5).join(", ");
    const more = names.length > 5 ? ` e mais ${names.length - 5}` : "";
    const sources = target.post_sources.map(source => SOURCE_LABELS[source] ?? source).join(" e ");
    const brandsText = brands.length === 1 ? `da marca ${shown}` : `de ${brands.length} marcas (${shown}${more})`;
    return `Menções ${brandsText}${sources ? `, de ${sources}` : ""}. Subtemas, mensagens-chave e porta-vozes: só aparecem os cadastrados em todas as marcas e fontes.`;
  })();

  // O destaque escolhido só vale enquanto continuar entre as opções:
  // se a pessoa escolhe um tema e depois manda o lote REMOVÊ-LO, a
  // escolha cai sozinha em vez de virar um lote inteiro de falhas.
  const themeNames = themeOptions.flatMap(option => option.ids.map(id => ({ id, name: option.name })));
  const highlightChoices = highlightOptions(target?.common_themes ?? [], state.themes, themeNames);
  const highlight =
    highlightChoices.find(option => option.name === state.highlight?.name) ?? null;
  const patch = buildBulkPatch({ ...state, highlight });
  const size = bulkPatchSize(patch);

  const setScalar = (field: "polarity" | "approach", raw: string) =>
    setState(current => ({
      ...current,
      [field]:
        raw === KEEP
          ? { touched: false, value: null }
          : { touched: true, value: raw || null }
    }));

  const setList = (
    field: "themes" | "keyMessages" | "spokespersons",
    update: (current: ListField) => ListField
  ) => setState(current => ({ ...current, [field]: update(current[field]) }));

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      await onSubmit(patch);
    } catch (failure) {
      setError(errorMessage(failure, "Não foi possível aplicar a correção."));
      setSaving(false);
    }
  };

  /** Cada opção aparece pelo PRIMEIRO id e, marcada, leva todos os ids
   *  dela (um por marca/fonte): o servidor aplica em cada menção o seu. */
  const listField = (
    field: "themes" | "keyMessages" | "spokespersons",
    label: string,
    options: BulkOption[]
  ) => {
    const current = state[field];
    const byFirstId = new Map(options.map(option => [option.ids[0], option] as const));
    return (
      <div className="bulk-list-field">
        <div className="classification-editor-field">
          <span>{label}</span>
          <Select
            value={current.mode}
            disabled={listsLocked}
            onChange={event =>
              setList(field, item => ({
                ...item,
                mode: event.target.value as ListField["mode"]
              }))
            }
          >
            <option value="keep">Não mexer</option>
            <option value="add">Adicionar a todas</option>
            <option value="remove">Remover de todas</option>
            <option value="replace">Substituir em todas por…</option>
          </Select>
        </div>
        {!listsLocked && current.mode !== "keep" &&
          (options.length ? (
            <CatalogMultiSelect
              label={label}
              options={options.map(option => ({
                id: option.ids[0],
                name: option.name,
                group: option.group
              }))}
              selected={current.ids}
              onToggle={id =>
                setList(field, item => {
                  const ids = byFirstId.get(id)?.ids ?? [id];
                  return {
                    ...item,
                    ids: item.ids.includes(id)
                      ? item.ids.filter(value => !ids.includes(value))
                      : [...new Set([...item.ids, ...ids])]
                  };
                })
              }
              onClear={() => setList(field, item => ({ ...item, ids: [] }))}
            />
          ) : (
            <p className="mention-detail-note">
              {`Nenhum item de ${label.toLowerCase()} está cadastrado em todas as marcas e fontes das menções escolhidas.`}
            </p>
          ))}
        {current.mode === "replace" && current.ids.length === 0 && (
          <p className="mention-detail-note">
            Substituir por nenhum item ESVAZIA {label.toLowerCase()} em todas
            as menções do lote.
          </p>
        )}
      </div>
    );
  };

  return createPortal(
    <div className="modal-overlay" onClick={saving ? undefined : onClose}>
      <div
        className="modal-container bulk-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="bulk-dialog-title"
        onClick={event => event.stopPropagation()}
      >
        <header className="modal-header">
          <div>
            <span className="eyebrow">EDIÇÃO EM LOTE</span>
            <h2 id="bulk-dialog-title">
              {count === null
                ? "Corrigir as menções do filtro"
                : `Corrigir ${count.toLocaleString("pt-BR")} ${count === 1 ? "menção" : "menções"}`}
            </h2>
            <p className="bulk-dialog-target">{targetLabel}</p>
          </div>
          <button
            className="icon-button"
            type="button"
            onClick={onClose}
            aria-label="Fechar"
            disabled={saving}
          >
            <X size={18} />
          </button>
        </header>
        <div className="modal-body">
          <div className="classification-editor">
            <div className="classification-editor-field">
              <span>Polaridade</span>
              <Select
                value={state.polarity.touched ? (state.polarity.value ?? "") : KEEP}
                onChange={event => setScalar("polarity", event.target.value)}
              >
                <option value={KEEP}>Não mexer</option>
                <option value="">Sem classificação</option>
                {polarityChoices.map(option => (
                  <option key={option.name} value={option.name}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </div>
            <div className="classification-editor-field">
              <span>Abordagem</span>
              <Select
                value={state.approach.touched ? (state.approach.value ?? "") : KEEP}
                onChange={event => setScalar("approach", event.target.value)}
              >
                <option value={KEEP}>Não mexer</option>
                <option value="">Sem classificação</option>
                {APPROACH_CHOICES.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </div>
            <div className="classification-editor-field">
              <span>Revisão</span>
              <Select
                value={state.reviewed}
                onChange={event =>
                  setState(current => ({
                    ...current,
                    reviewed: event.target.value as "keep" | "yes" | "no"
                  }))
                }
              >
                <option value="keep">Não mexer</option>
                <option value="yes">Marcar como revisada</option>
                <option value="no">Desmarcar revisão</option>
              </Select>
            </div>
            <p className="mention-detail-note" role="status">
              {targetNote}
            </p>
            {listField("themes", "Subtemas", themeOptions)}
            <div className="bulk-list-field">
              <div className="classification-editor-field">
                <span>Subtema em destaque</span>
                <Select
                  value={highlight?.name ?? KEEP}
                  disabled={listsLocked || highlightChoices.length === 0}
                  onChange={event =>
                    setState(current => ({
                      ...current,
                      highlight:
                        highlightChoices.find(
                          option => option.name === event.target.value
                        ) ?? null
                    }))
                  }
                >
                  <option value={KEEP}>Não mexer</option>
                  {highlightChoices.map(option => (
                    <option key={option.name} value={option.name}>
                      {option.name}
                    </option>
                  ))}
                </Select>
              </div>
              {!listsLocked && (
                <p className="mention-detail-note">
                  {highlightChoices.length === 0
                    ? "Nenhum subtema está em todas as menções escolhidas, então não há destaque para definir em lote. Adicione um subtema a todas, acima, para poder destacá-lo."
                    : "Só aparecem os subtemas presentes em todas as menções escolhidas."}
                </p>
              )}
            </div>
            {listField("keyMessages", "Mensagens-chave", keyMessageOptions)}
            {listField("spokespersons", "Porta-vozes", spokespersonOptions)}
            <p className="mention-detail-note">
              Cada menção recebe a própria linha de histórico e o recálculo
              do CENA, como na correção individual. Menção que já está como
              o lote pede não muda nem deixa rastro.
            </p>
            {error && (
              <div className="account-feedback error" aria-live="polite">
                {error}
              </div>
            )}
          </div>
        </div>
        <footer className="modal-footer">
          <button
            type="button"
            className="secondary-button"
            onClick={onClose}
            disabled={saving}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="primary-button"
            onClick={() => void submit()}
            disabled={saving || isBulkPatchEmpty(patch)}
          >
            {saving ? (
              <LoaderCircle className="spin" size={16} />
            ) : size === 0 ? (
              "Escolha o que corrigir"
            ) : (
              `Aplicar ${size} ${size === 1 ? "correção" : "correções"}`
            )}
          </button>
        </footer>
      </div>
    </div>,
    document.body
  );
}
